import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useMeta } from "@/api/meta";
import { useCompany, useUpdateCompany, type Company } from "@/api/queries";
import { useAuth, useMe } from "@/app/auth";
import { Field } from "@/components/common/Field";
import { TimezoneSelect } from "@/components/common/TimezoneSelect";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input, Label, NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { languages, type Language } from "@/i18n";
import { errorText, fieldError } from "@/lib/errors";

import { Section } from "./SettingsLayout";

export function CompanyPage() {
  const { t } = useTranslation();
  const company = useCompany();
  if (company.isPending) return <Skeleton className="h-96" />;
  if (company.isError) return <Alert variant="danger">{errorText(t, company.error)}</Alert>;
  return <CompanyForm company={company.data} />;
}

function CompanyForm({ company }: { company: Company }) {
  const { t } = useTranslation();
  const me = useMe();
  const auth = useAuth();
  const meta = useMeta();
  const update = useUpdateCompany();
  const [name, setName] = useState(company.name);
  const [timezone, setTimezone] = useState(company.timezone);
  const [locale, setLocale] = useState<Language>(company.default_locale);
  const [requireMfa, setRequireMfa] = useState(company.require_mfa_for_staff);
  const [managerDelete, setManagerDelete] = useState(company.manager_can_delete_screenshots);
  const [backfill, setBackfill] = useState(String(company.max_backfill_hours));
  const [dailyHours, setDailyHours] = useState(String(company.max_daily_hours));
  const [saved, setSaved] = useState(false);
  const dirty =
    name.trim() !== company.name ||
    timezone !== company.timezone ||
    locale !== company.default_locale ||
    requireMfa !== company.require_mfa_for_staff ||
    managerDelete !== company.manager_can_delete_screenshots ||
    Number(backfill) !== company.max_backfill_hours ||
    Number(dailyHours) !== company.max_daily_hours;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    try {
      const next = await update.mutateAsync({
        name: name.trim(),
        timezone,
        default_locale: locale,
        require_mfa_for_staff: requireMfa,
        manager_can_delete_screenshots: managerDelete,
        max_backfill_hours: Number(backfill),
        max_daily_hours: Number(dailyHours),
      });
      if (next.name !== me.company_name) auth.setMe({ ...me, company_name: next.name });
      setSaved(true);
    } catch {
      // Shown below.
    }
  };

  const err = update.error;
  const mfaError = fieldError(t, err, "require_mfa_for_staff");
  return (
    <Card className="px-6 py-6">
      <form onSubmit={(e) => void submit(e)}>
        <Section title={t("settings.company.title")} description={t("settings.company.description")}>
          <div className="flex flex-col gap-4">
            <Field label={t("settings.company.name")} error={fieldError(t, err, "name")}>
              <Input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <Field label={t("settings.company.timezone")}>
                <TimezoneSelect value={timezone} onChange={(e) => setTimezone(e.target.value)} />
              </Field>
              <Field label={t("settings.company.locale")}>
                <NativeSelect
                  className="h-10"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value as Language)}
                >
                  {languages.map((l) => (
                    <option key={l} value={l}>
                      {t(`languages.${l}`)}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </div>
          </div>
        </Section>
        <Section title={t("settings.company.security")}>
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <Switch
                id="require-mfa"
                checked={requireMfa}
                onCheckedChange={setRequireMfa}
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="require-mfa">{t("settings.company.requireMfa")}</Label>
                <p className="text-sm text-muted">{t("settings.company.requireMfaHint")}</p>
              </div>
            </div>
            {mfaError && <Alert variant="warning">{mfaError}</Alert>}
          </div>
        </Section>
        <Section title={t("settings.company.monitoring")}>
          <div className="flex items-start gap-3">
            <Switch
              id="manager-delete"
              checked={managerDelete}
              onCheckedChange={setManagerDelete}
              className="mt-0.5"
            />
            <div>
              <Label htmlFor="manager-delete">{t("settings.company.managerDelete")}</Label>
              <p className="text-sm text-muted">{t("settings.company.managerDeleteHint")}</p>
            </div>
          </div>
        </Section>
        <Section title={t("settings.company.integrity")} description={t("settings.company.integrityHint")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("settings.company.backfillHours")}
              hint={t("settings.company.backfillHoursHint")}
              error={fieldError(t, err, "max_backfill_hours")}
            >
              <Input
                type="number"
                min={1}
                max={720}
                required
                value={backfill}
                onChange={(e) => setBackfill(e.target.value)}
              />
            </Field>
            <Field
              label={t("settings.company.dailyHours")}
              hint={t("settings.company.dailyHoursHint")}
              error={fieldError(t, err, "max_daily_hours")}
            >
              <Input
                type="number"
                min={1}
                max={24}
                required
                value={dailyHours}
                onChange={(e) => setDailyHours(e.target.value)}
              />
            </Field>
          </div>
        </Section>
        <Section
          title={t("settings.company.email")}
          aside={
            meta.data && (
              <Badge variant={meta.data.email_enabled ? "success" : "warning"}>
                {meta.data.email_enabled ? t("settings.mfa.on") : t("settings.mfa.off")}
              </Badge>
            )
          }
        >
          <p className="text-muted">
            {meta.data?.email_enabled ? t("settings.company.emailOn") : t("settings.company.emailOff")}
          </p>
        </Section>
        <div className="flex flex-col gap-3 border-t pt-5">
          {err && !mfaError && !fieldError(t, err, "name") && (
            <Alert variant="danger">{errorText(t, err)}</Alert>
          )}
          {saved && !dirty && <Alert variant="success">{t("settings.company.saved")}</Alert>}
          <div>
            <Button type="submit" disabled={update.isPending || !dirty || !name.trim()}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
}
